import { createApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import axios from 'axios'
import { applyGlobalVisitDefaults } from '@/utils/inertiaVisitDefaults'
import { installCsrfRetry } from '@/utils/csrfRetry'
import './style.css'
import { registerSW } from 'virtual:pwa-register'
import {
  bootWithVisitGate,
  createNotificationVisitGate,
  listenForNotificationOpen,
} from '@/composables/useWebPush'

// Configure axios for standalone API calls (NOT used by Inertia.js)
// Inertia.js handles CSRF and requests independently
axios.defaults.withCredentials = true
axios.defaults.headers.common['Accept'] = 'application/json'
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

// One retry after a 403 csrf_error (expired token) for fetch and axios, which Inertia
// visits use too — see utils/csrfRetry.js.
installCsrfRetry(axios)

// Note: xsrfCookieName and xsrfHeaderName are already axios defaults ('XSRF-TOKEN' and 'X-XSRF-TOKEN')
// so no need to set them explicitly

// IMPORTANT: Do NOT set Content-Type globally - let axios/browser determine it
// This allows FormData to work properly with multipart/form-data
// JSON requests will automatically get application/json when sending objects

// Force dark mode globally
if (typeof document !== 'undefined') {
  document.documentElement.classList.add('dark')
  document.documentElement.setAttribute('data-theme', 'dark')
}

// CSRF token + partial-reload `errors` for every Inertia visit — see
// utils/inertiaVisitDefaults.js for what and why.
router.on('before', (event) => {
  applyGlobalVisitDefaults(event.detail.visit)
})

// Global 401 session expiry handler
router.on('error', (errors) => {
  // Check if this is a 401 authentication error
  if (
    errors &&
    (errors.status === 401 ||
      errors.message?.includes('401') ||
      errors.message?.includes('Unauthorized'))
  ) {
    console.warn('Session expired - redirecting to login')
    window.location.href = `${window.location.protocol}//${window.location.host}/login`
    return false // Prevent default error handling
  }
})

// A notification click can arrive before the app has its first page; the gate holds it until the app is mounted.
const notificationVisits = createNotificationVisitGate((url) => router.visit(url))

bootWithVisitGate(createInertiaApp, notificationVisits, {
  // Progress indicator configuration - keep default behavior
  progress: {
    color: '#4F46E5',
    showSpinner: true,
  },
  resolve: async (name) => {
    try {
      let component

      // Dynamic imports for better error handling - handle both lowercase and exact case
      switch (name.toLowerCase()) {
        case 'login':
          component = (await import('./Pages/Login.vue')).default
          break
        case 'tfaverify':
        case 'tfa-verify':
          component = (await import('./Pages/TfaVerify.vue')).default
          break
        case 'tfasetup':
        case 'tfa-setup':
          component = (await import('./Pages/TfaSetup.vue')).default
          break
        case 'tfasettings':
        case 'tfa-settings':
          component = (await import('./Pages/TfaSettings.vue')).default
          break
        case 'profile':
          component = (await import('./Pages/Profile.vue')).default
          break
        case 'dashboard':
          component = (await import('./Pages/Dashboard.vue')).default
          break
        case 'clients':
          component = (await import('./Pages/Clients.vue')).default
          break
        case 'contacts':
          component = (await import('./Pages/Contacts.vue')).default
          break
        case 'messages':
          component = (await import('./Pages/Messages.vue')).default
          break
        case 'settings':
          component = (await import('./Pages/Settings.vue')).default
          break
        case 'users':
          component = (await import('./Pages/Users.vue')).default
          break
        case 'calls':
          component = (await import('./Pages/Calls.vue')).default
          break
        case 'calldetails':
          component = (await import('./Pages/CallDetails.vue')).default
          break
        case 'businessprofiles':
          component = (await import('./Pages/BusinessProfiles.vue')).default
          break
        case 'businessprofileeditor':
          component = (await import('./Pages/BusinessProfileEditor.vue')).default
          break
        case 'businessprofileform':
          component = (await import('./Pages/BusinessProfileForm.vue')).default
          break
        case 'prompteditor':
          component = (await import('./Pages/PromptEditor.vue')).default
          break
        case 'phonenumbers/transfers':
          component = (await import('./Pages/PhoneNumbers/Transfers.vue')).default
          break
        case 'phonenumbers':
          component = (await import('./Pages/PhoneNumbers.vue')).default
          break
        case 'phonenumbereditor':
          component = (await import('./Pages/PhoneNumbers.vue')).default
          break
        case 'analytics':
          component = (await import('./Pages/Analytics.vue')).default
          break
        case 'billing':
          component = (await import('./Pages/Billing.vue')).default
          break
        case 'integrations':
          component = (await import('./Pages/Integrations.vue')).default
          break
        case 'integrationform':
          component = (await import('./Pages/IntegrationForm.vue')).default
          break
        case 'integrationlogs':
          component = (await import('./Pages/IntegrationLogs.vue')).default
          break
        case 'businessdata':
          component = (await import('./Pages/BusinessData/BusinessData.vue')).default
          break
        case 'itemform':
          component = (await import('./Pages/BusinessData/ItemForm.vue')).default
          break
        case 'prospects':
          component = (await import('./Pages/Prospects.vue')).default
          break
        case 'documents':
          component = (await import('./Pages/Documents.vue')).default
          break
        case 'promptvalidationhistory':
          component = (await import('./Pages/PromptValidationHistory.vue')).default
          break
        case 'problemcallers':
          component = (await import('./Pages/ProblemCallers.vue')).default
          break
        case 'notifications':
          component = (await import('./Pages/Notifications.vue')).default
          break
        case 'billing/reconciliation':
          component = (await import('./Pages/Billing/Reconciliation.vue')).default
          break
        case 'billing/carriercostaccounts':
          component = (await import('./Pages/Billing/CarrierCostAccounts.vue')).default
          break
        case 'appointments':
          component = (await import('./Pages/Appointments.vue')).default
          break
        case 'error':
          component = (await import('./Pages/Error.vue')).default
          break
        default:
          console.error(`❌ Component ${name} not found!`)
          try {
            component = (await import('./Pages/Error.vue')).default
          } catch (errorImportError) {
            console.error('❌ Failed to import Error component:', errorImportError)
            component = {
              template: `
                 <div class="p-8 text-center">
                   <h2 class="text-xl font-bold text-red-600 mb-4">Component Not Found</h2>
                   <p class="text-gray-600">Component "${name}" could not be loaded</p>
                   <button onclick="window.location.href='/'" class="mt-4 px-4 py-2 bg-blue-500 text-white rounded">
                     Return to Dashboard
                   </button>
                 </div>
               `,
            }
          }
      }

      return component
    } catch (error) {
      console.error('❌ Error loading component:', name, error)

      // Fallback to a simple error component
      return {
        template: `
          <div class="p-8 text-center">
            <h2 class="text-xl font-bold text-red-600 mb-4">Component Error</h2>
            <p class="text-gray-600">Failed to load component: ${name}</p>
            <p class="text-sm text-gray-500 mt-2">Error: ${error.message}</p>
            <button @click="$inertia.visit('/')" class="mt-4 px-4 py-2 bg-blue-500 text-white rounded">
              Return to Dashboard
            </button>
          </div>
        `,
      }
    }
  },
  setup({ el, App, props, plugin }) {
    const app = createApp({ render: () => h(App, props) }).use(plugin)

    app.mount(el)
  },
})

// Register the service worker (vite-plugin-pwa, injectRegister: false). Skipped without
// support or a secure context; the worker caches nothing, so the app works the same without it.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  registerSW({
    immediate: true,
    onRegisterError(error) {
      console.error('Service worker registration failed', { operation: 'registerSW', error })
    },
  })
  // A notification click asks the page to open /notifications; every page listens, the login page included.
  listenForNotificationOpen(notificationVisits.visit)
}
